import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Link } from "gatsby";
import { useMeasure } from "react-use";
import { GatsbyImage } from "gatsby-plugin-image";
import { AspectRatioImageContainer } from "../containers/aspect-ratio-image-container";
import { ArrowNext, ArrowPrev } from "../icons/arrows";
import { formatEventDate } from "../utils/format-date";

const CustomCarouselContainer = styled.div`
  margin: 0;
  background-color: #fff;

  & .title-bar {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    align-items: baseline;
    margin: 0 0 30px 115px;
    padding: 30px 50px 0 0;

    @media (max-width: 1445px) {
      margin-left: 115px;
    }
    @media (max-width: 999px) {
      margin-left: 100px;
    }
    @media (max-width: 800px) {
      margin-left: 25px;
    }

    & .navigation {
      display: flex;
      align-items: center;
      @media (max-width: 500px) {
        display: none;
      }

      & button {
        padding: 0;
        width: 25px;
        height: 11px;
      }

      & svg.line {
        height: 11px;
        margin: 0 5px;
      }

      & p {
        margin: 0;
        &.spacer {
          text-align: center;
          width: 20px;
        }
      }
    }
  }

  & .single-item {
    & img {
      width: 100%;
      height: 100%;

      object-fit: cover;
    }

    & .text-container {
      margin-top: 20px;

      & h4,
      & p {
        font-size: 14px;
        line-height: 25px;
        margin: 0;
      }
    }
  }

  & .slide-container {
    display: flex;
    overflow-x: scroll;
    scroll-snap-type: x mandatory;
    scroll-behavior: smooth;
    padding-bottom: 30px;

    &::-webkit-scrollbar {
      display: none;
    }

    & .single-item {
      min-width: 31vw;
      margin-right: 40px;
      scroll-snap-align: center;

      &:first-of-type {
        margin-left: 115px;
      }
      &:last-of-type {
        margin-right: 50px;
      }

      @media (max-width: 500px) {
        min-width: 80vw;
        margin-right: 25px;
        margin-left: 25px;
      }
    }
  }
  & .bottom-bar {
    padding: 30px 0 50px 0;
    margin: 0 0 30px 115px;

    & .button {
      border: 1px solid #09184f;
      padding: 8px 12px;

      @media (max-width: 800px) {
        width: 100%;
        text-align: center;
      }
    }
    & .button:hover {
      background-color: #09184f;
      color: #fff;
      transition: all 0.3s ease;
    }
  }
`;

const SingleItem = ({ item, setItemWidth }) => {
  const [ref, { width }] = useMeasure();

  useEffect(() => {
    setItemWidth(width);
  }, [width]);

  const image = (
    <AspectRatioImageContainer image={null} padding={140}>
      {item.image?.gatsbyImageData && (
        <GatsbyImage
          image={item.image.gatsbyImageData}
          alt={item.image.alt !== null ? item.image.alt : ``}
        />
      )}
    </AspectRatioImageContainer>
  );

  return (
    <div className="single-item" ref={ref}>
      <div className="image-container">
        {item.url ? <Link to={item.url}>{image}</Link> : image}
      </div>

      <div className="text-container">
        <h4 className="title uppercase gotham-light">{item.title}</h4>
        {item.subtitle && (
          <p className="subtitle gotham-light">{item.subtitle}</p>
        )}
      </div>
    </div>
  );
};

// Presentation only: `items` are already normalised (see
// `normaliseCarouselDocument`), the title, the button label and the button link
// are plain strings. Used both by the Prismic slice below and directly by the
// gallery template, which builds its carousel without any slice.
export const CustomCarouselLayout = ({
  items,
  title,
  buttonText,
  buttonLink,
}) => {
  const [itemWidth, setItemWidth] = useState(0);
  const gallery = useRef();
  const [currentSection, setCurrentSection] = useState(1);

  if (items.length === 0) return null;

  const slides = items.map((item, i) => (
    <SingleItem
      key={`carousel_slide_${item.id}_${i}`}
      item={item}
      setItemWidth={setItemWidth}
    />
  ));

  const numberOfSlideSections = Math.ceil(slides.length / 3);

  const scrollToNextPage = () => {
    gallery.current.scrollBy({ left: itemWidth * 3, behavior: "smooth" });
    if (currentSection < numberOfSlideSections)
      setCurrentSection((prev) => prev + 1);
  };

  const scrollToPrevPage = () => {
    gallery.current.scrollBy({ left: -itemWidth * 3, behavior: "smooth" });
    if (currentSection > 1) setCurrentSection((prev) => prev - 1);
  };

  return (
    <CustomCarouselContainer className="custom-carousel module">
      <div className="title-bar">
        <div className="carousel-title">
          <p className="tag uppercase gotham-bold">{title}</p>
        </div>
        <div className="navigation">
          <button onClick={scrollToPrevPage}>
            <ArrowPrev />
          </button>
          <p className="gotham-bold tag">{currentSection}</p>
          <p className="gotham-bold tag spacer">—</p>
          <p className="gotham-bold tag">{numberOfSlideSections}</p>
          <button onClick={scrollToNextPage}>
            <ArrowNext />
          </button>
        </div>
      </div>
      <div className="slide-container" ref={gallery}>
        {slides}
      </div>
      <div className="bottom-bar">
        {buttonText && buttonLink && (
          <Link to={buttonLink} className="button">
            {buttonText}
          </Link>
        )}
      </div>
    </CustomCarouselContainer>
  );
};

// An event, a project and an article are three custom types with three
// different shapes: flatten them into the single set of fields a card renders.
// `labels` carries the localised site labels, only read for an event's date
// range; `formatEventDate` falls back to English when it is missing.
export const normaliseCarouselDocument = (doc, labels = {}) => ({
  id: doc.id,
  url: doc.url,
  title: doc.data.title?.text,
  // Two custom types, two raw field names for the same picture: an event stores
  // it in `featured_img`, a project and an article in `thumbnail`.
  image: doc.data.thumbnail || doc.data.featured_img,
  // One line of subtitle, chosen on the Prismic document type: the dates for an
  // event, the localised project type for a project, nothing for an article
  // (`excerpt_text` is a paragraph, unreadable on a 14px line).
  subtitle:
    doc.type === `event`
      ? formatEventDate(
          doc.data.date_begins,
          doc.data.date_ends,
          labels,
          doc.lang
        )
      : doc.type === `project`
      ? doc.lang === `fr-fr`
        ? doc.data.project_type_french
        : doc.data.project_type
      : null,
});

// Where the button underneath the carousel sends the visitor: the category the
// whole carousel belongs to, read off its first item.
//
// An event has no category page of its own — its category is a
// `galleries_single` or an `eventspage` whose own URL points somewhere else — so
// the link is rebuilt on the `/{lang}/event/category/{uid}` route that
// gatsby-node.js generates. A project and an article do have one.
const categoryLink = (doc) => {
  const category = doc.data.categories?.[0]?.category;

  if (category == null) return null;

  if (doc.type === `event`) {
    if (category.uid == null) return null;
    return `/${doc.lang.split("-")[0]}/event/category/${category.uid}`;
  }

  return category.document?.url;
};

export const CustomCarousel = ({ content, labels }) => {
  // A Prismic link can be left empty, and a linked document can have been
  // unpublished: reading `document.data` unguarded blanks the whole page.
  const linkedDocuments = content.items
    .map((item) => item.content_link?.document)
    .filter((doc) => doc?.data != null);

  if (linkedDocuments.length === 0) return null;

  // Every item of a carousel belongs to the same category, hence to the same
  // custom type. An editor can still link a document of another type by
  // mistake: drop it, otherwise its card shows a subtitle of the wrong kind and
  // the button below points at a category the rest of the carousel is not in.
  const [firstDocument] = linkedDocuments;
  const documents = linkedDocuments.filter(
    (doc) => doc.type === firstDocument.type
  );

  return (
    <CustomCarouselLayout
      items={documents.map((doc) => normaliseCarouselDocument(doc, labels))}
      title={content.primary.carousel_title?.text}
      buttonText={content.primary.button_text}
      buttonLink={categoryLink(firstDocument)}
    />
  );
};
